// @ts-strict-ignore
import { JsonObject, JsonProperty } from '@shared/convert/convert-core';
import { PropertyVO } from '../../components/article/model/vo/property.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { MetaElementUsageIdentityVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { RelativePositionReferenceArticleLocationVO } from '../../components/article/model/vo/relative-position-reference-article-location.vo';

@JsonObject('ValidationContextVO')
export class ValidationContextVO {
  @JsonProperty('id', 'String')
  public lang: string = undefined;

  @JsonProperty('type', 'String')
  public type: string = undefined;

  @JsonProperty('onlySingleArticleProposal', 'Boolean')
  public onlySingleArticleProposal: boolean = undefined;

  constructor(lang: string = null, type: string = null) {
    this.lang = lang;
    this.type = type;
    this.onlySingleArticleProposal = false;
  }
}

@JsonObject('ArticleDescriptorVO')
export class ArticleDescriptorVO {
  public static readonly CLASS_NAME: string = 'ArticleDescriptorVO';

  @JsonProperty('type', 'string')
  public type: string = undefined;

  public constructor() {
    this.type = ArticleDescriptorVO.CLASS_NAME;
  }
}

@JsonObject('ListArticleDescriptorVO')
export class ListArticleDescriptorVO extends ArticleDescriptorVO {
  public static readonly CLASS_NAME: string = 'ListArticleDescriptorVO';

  @JsonProperty('articles', '[ArticleVO]')
  public articles: ArticleVO[] = undefined;

  public constructor() {
    super();
    this.type = ListArticleDescriptorVO.CLASS_NAME;
  }
}

@JsonObject('SingleArticleDescriptorVO')
export class SingleArticleDescriptorVO extends ArticleDescriptorVO {
  public static readonly CLASS_NAME: string = 'SingleArticleDescriptorVO';

  @JsonProperty('article', 'ArticleVO')
  public article: ArticleVO = undefined;

  public constructor() {
    super();
    this.type = SingleArticleDescriptorVO.CLASS_NAME;
  }
}

@JsonObject('UsageArticleDescriptorVO')
export class UsageArticleDescriptorVO extends ArticleDescriptorVO {
  public static readonly CLASS_NAME: string = 'UsageArticleDescriptorVO';

  @JsonProperty('articleUsageId', 'String')
  public articleUsageId: string = undefined;

  public constructor() {
    super();
    this.type = UsageArticleDescriptorVO.CLASS_NAME;
  }
}

@JsonObject('QuickFixActionVO')
export class QuickFixActionVO {
  public static readonly CLASS_NAME: string = 'QuickFixActionVO';

  @JsonProperty('type', 'string')
  public type: string = undefined;

  @JsonProperty('nr', 'Number')
  public nr: number = undefined;

  @JsonProperty('message', 'string')
  public message: string = undefined;

  public constructor() {
    this.type = QuickFixActionVO.CLASS_NAME;
  }
}

@JsonObject('InsertArticleQuickFixActionVO')
export class InsertArticleQuickFixActionVO extends QuickFixActionVO {
  public static readonly CLASS_NAME: string = 'InsertArticleQuickFixActionVO';

  @JsonProperty('articleDescriptor', 'ArticleDescriptorVOConverter', true)
  public articleDescriptor: ArticleDescriptorVO = undefined;

  @JsonProperty('articleLocation', 'RelativePositionReferenceArticleLocationVO')
  public articleLocation: RelativePositionReferenceArticleLocationVO = undefined;

  public constructor() {
    super();
    this.type = InsertArticleQuickFixActionVO.CLASS_NAME;
  }
}

@JsonObject('DeleteArticleQuickFixActionVO')
export class DeleteArticleQuickFixActionVO extends QuickFixActionVO {
  public static readonly CLASS_NAME: string = 'DeleteArticleQuickFixActionVO';

  @JsonProperty('articleDescriptor', 'ArticleDescriptorVOConverter', true)
  public articleDescriptor: ArticleDescriptorVO = undefined;

  @JsonProperty('closeGap', 'Boolean')
  public closeGap: boolean = undefined;

  @JsonProperty('gapLeftArticleDescriptor', 'ArticleDescriptorVOConverter', true)
  public gapLeftArticleDescriptor: ArticleDescriptorVO = undefined;

  @JsonProperty('gapRightArticleDescriptor', 'ArticleDescriptorVOConverter', true)
  public gapRightArticleDescriptor: ArticleDescriptorVO = undefined;

  public constructor() {
    super();
    this.type = DeleteArticleQuickFixActionVO.CLASS_NAME;
  }
}

@JsonObject('ApplyTextToTextureAreaUsageQuickFixActionVO')
export class ApplyTextToTextureAreaUsageQuickFixActionVO extends QuickFixActionVO {
  public static readonly CLASS_NAME: string = 'ApplyTextToTextureAreaUsageQuickFixActionVO';

  @JsonProperty('textureAreaUsageIdentity', 'MetaElementUsageIdentityVO')
  public textureAreaUsageIdentity: MetaElementUsageIdentityVO = undefined;

  @JsonProperty('textValue', 'TextValueVO')
  public textValue: TextValueVO = undefined;

  public constructor() {
    super();
    this.type = ApplyTextToTextureAreaUsageQuickFixActionVO.CLASS_NAME;
  }
}

@JsonObject('QuickFixVO')
export class QuickFixVO {
  @JsonProperty('nr', 'Number')
  public nr: number = 0;

  @JsonProperty('execPriority', 'Number')
  public execPriority: number = undefined;

  @JsonProperty('actions', 'QuickFixActionVOsConverter', true)
  public actions: QuickFixActionVO[] = undefined;

  @JsonProperty('message', 'string')
  public message: string = undefined;
}

@JsonObject('ElementReferenceVO')
export class ElementReferenceVO {
  public static readonly CLASS_NAME: string = 'ElementReferenceVO';

  @JsonProperty('type', 'string')
  public type: string = undefined;

  public equals(obj): boolean {
    return obj instanceof ElementReferenceVO;
  }

  public constructor() {
    this.type = ElementReferenceVO.CLASS_NAME;
  }
}

@JsonObject('ArticleReferenceVO')
export class ArticleReferenceVO extends ElementReferenceVO {
  public static readonly CLASS_NAME: string = 'ArticleReferenceVO';

  @JsonProperty('articleUsageId', 'String')
  public articleUsageId: string = undefined;

  public constructor() {
    super();
    this.type = ArticleReferenceVO.CLASS_NAME;
  }

  public equals(obj): boolean {
    if (!(obj instanceof ArticleReferenceVO)) {
      return false;
    }
    if (obj.articleUsageId !== this.articleUsageId) {
      return false;
    }
    return super.equals(obj);
  }
}

@JsonObject('ArticleUsageInfoPropertyReferenceVO')
export class ArticleUsageInfoPropertyReferenceVO extends ElementReferenceVO {
  public static readonly CLASS_NAME: string = 'ArticleUsageInfoPropertyReferenceVO';

  @JsonProperty('articleReference', 'ArticleReferenceVO')
  public articleReference: ArticleReferenceVO = undefined;

  @JsonProperty('propertyName', 'String')
  public propertyName: string = undefined;

  public constructor() {
    super();
    this.type = ArticleUsageInfoPropertyReferenceVO.CLASS_NAME;
  }

  public equals(obj): boolean {
    if (!(obj instanceof ArticleUsageInfoPropertyReferenceVO)) {
      return false;
    }
    if (obj.propertyName !== this.propertyName) {
      return false;
    }
    if (!this.articleReference) {
      if (obj.articleReference) {
        return false;
      }
    } else {
      if (!this.articleReference.equals(obj.articleReference)) {
        return false;
      }
    }
    return super.equals(obj);
  }
}

@JsonObject('ArticleUsagePlausiPropertyReferenceVO')
export class ArticleUsagePlausiPropertyReferenceVO extends ElementReferenceVO {
  public static readonly CLASS_NAME: string = 'ArticleUsagePlausiPropertyReferenceVO';

  @JsonProperty('articleReference', 'ArticleReferenceVO')
  public articleReference: ArticleReferenceVO;

  @JsonProperty('propertyName', 'String')
  public propertyName: string = undefined;

  public constructor() {
    super();
    this.type = ArticleUsagePlausiPropertyReferenceVO.CLASS_NAME;
  }

  public equals(obj): boolean {
    if (!(obj instanceof ArticleUsagePlausiPropertyReferenceVO)) {
      return false;
    }
    if (obj.propertyName !== this.propertyName) {
      return false;
    }
    if (!this.articleReference) {
      if (obj.articleReference) {
        return false;
      }
    } else {
      if (!this.articleReference.equals(obj.articleReference)) {
        return false;
      }
    }
    return super.equals(obj);
  }
}

@JsonObject('CalculatedArticlePropertyReferenceVO')
export class CalculatedArticlePropertyReferenceVO extends ElementReferenceVO {
  public static readonly CLASS_NAME: string = 'CalculatedArticlePropertyReferenceVO';

  @JsonProperty('articleReference', 'ArticleReferenceVO')
  public articleReference: ArticleReferenceVO = undefined;

  @JsonProperty('propertyName', 'String')
  public propertyName: string = undefined;

  public constructor() {
    super();
    this.type = CalculatedArticlePropertyReferenceVO.CLASS_NAME;
  }

  public equals(obj): boolean {
    if (!(obj instanceof CalculatedArticlePropertyReferenceVO)) {
      return false;
    }
    if (obj.propertyName !== this.propertyName) {
      return false;
    }
    if (!this.articleReference) {
      if (obj.articleReference) {
        return false;
      }
    } else {
      if (!this.articleReference.equals(obj.articleReference)) {
        return false;
      }
    }
    return super.equals(obj);
  }
}

@JsonObject('DefinedMetaElementPlausiPropertyReferenceVO')
export class DefinedMetaElementPlausiPropertyReferenceVO extends ElementReferenceVO {
  public static readonly CLASS_NAME: string = 'DefinedMetaElementPlausiPropertyReferenceVO';

  @JsonProperty('articleReference', 'ArticleReferenceVO')
  public articleReference: ArticleReferenceVO = undefined;

  @JsonProperty('metaElementName', 'String')
  public metaElementName: string = undefined;

  @JsonProperty('propertyName', 'String')
  public propertyName: string = undefined;

  public constructor() {
    super();
    this.type = DefinedMetaElementPlausiPropertyReferenceVO.CLASS_NAME;
  }

  public equals(obj): boolean {
    if (!(obj instanceof DefinedMetaElementPlausiPropertyReferenceVO)) {
      return false;
    }
    if (obj.propertyName !== this.propertyName) {
      return false;
    }
    if (obj.metaElementName !== this.metaElementName) {
      return false;
    }
    if (!this.articleReference) {
      if (obj.articleReference) {
        return false;
      }
    } else {
      if (!this.articleReference.equals(obj.articleReference)) {
        return false;
      }
    }
    return super.equals(obj);
  }
}

@JsonObject('RuleResultVO')
export class RuleResultVO {
  public static readonly SEVERITY_UNKNOWN: number = 0;
  public static readonly SEVERITY_INFO: number = 1;
  public static readonly SEVERITY_WARNING: number = 2;
  public static readonly SEVERITY_ERROR: number = 3;

  @JsonProperty('errorCode', 'Number')
  public errorCode: number = undefined;

  @JsonProperty('severity', 'Number')
  public severity: number = undefined;

  @JsonProperty('errorMessage', 'string')
  public errorMessage: string = undefined;

  @JsonProperty('ruleName', 'String')
  public ruleName: string = undefined;

  @JsonProperty('quickFixes', '[QuickFixVO]')
  public quickFixes: QuickFixVO[] = undefined;

  @JsonProperty('affectedElements', 'ElementReferenceVOsConverter', true)
  public affectedElements: ElementReferenceVO[] = undefined;

  @JsonProperty('selectedQuickFixNr', 'Number')
  public selectedQuickFixNr: number = undefined;

  @JsonProperty('defaultQuickFixNr', 'Number')
  public defaultQuickFixNr: number = undefined;

  public getSelectedQuickFix(): QuickFixVO {
    return this.getQuickFix(this.selectedQuickFixNr);
  }

  public getQuickFix(quickFixNr: number): QuickFixVO {
    if (ArrayUtils.isEmpty(this.quickFixes)) {
      return null;
    }
    for (let i = 0; i < this.quickFixes.length; i++) {
      const quickFix: QuickFixVO = this.quickFixes[i];
      if (quickFix.nr === quickFixNr) {
        return quickFix;
      }
    }
    return null;
  }

  public equals(ruleResult: RuleResultVO): boolean {
    if (this.errorCode !== ruleResult.errorCode || this.affectedElements.length !== ruleResult.affectedElements.length) {
      return false;
    }

    for (let i = 0; i < this.affectedElements.length; i++) {
      if ((this.affectedElements[i] as any).articleUsageId !== (ruleResult.affectedElements[i] as any).articleUsageId) {
        return false;
      }
    }

    return true;
  }
}

@JsonObject('ArticleCalculationResultVO')
export class ArticleCalculationResultVO {
  @JsonProperty('articleUsageId', 'String')
  public articleUsageId: string = undefined;

  @JsonProperty('calculatedArticleProperties', '[PropertyVO]')
  public calculatedArticleProperties: PropertyVO[] = undefined;
}

@JsonObject('DeferredArticlePropertiesResultVO')
export class DeferredArticlePropertiesResultVO {
  @JsonProperty('articleUsageId', 'String')
  public articleUsageId: string = undefined;

  @JsonProperty('addedUsagePlausiProperties', '[PropertyVO]')
  public addedUsagePlausiProperties: PropertyVO[] = undefined;
}

@JsonObject('ValidationResultVO')
export class ValidationResultVO {

  @JsonProperty('ruleResults', '[RuleResultVO]')
  public ruleResults: RuleResultVO[] = undefined;

  @JsonProperty('articleCalculationResults', '[ArticleCalculationResultVO]')
  public articleCalculationResults: ArticleCalculationResultVO[] = undefined;

  @JsonProperty('deferredArticlePropertiesResults', '[DeferredArticlePropertiesResultVO]')
  public deferredArticlePropertiesResults: DeferredArticlePropertiesResultVO[] = undefined;
}

@JsonObject('IOConsumptionResultVO')
export class IOConsumptionResultVO {

  @JsonProperty('articleConsumption', '[ArticleConsumption]')
  public articleConsumption: ArticleConsumption[] = undefined;

}

@JsonObject('ArticleConsumption')
export class ArticleConsumption {

  @JsonProperty('articleReference', 'String')
  public articleReference: string = undefined;

  @JsonProperty('maxBusCurrent', 'Number')
  public maxBusCurrent: number = undefined;

  @JsonProperty('maxFieldCurrent', 'Number')
  public maxFieldCurrent: number = undefined;

  @JsonProperty('remainingBusCurrent', 'Number')
  public remainingBusCurrent: number = undefined;

  @JsonProperty('remainingFieldCurrent', 'Number')
  public remainingFieldCurrent: number = undefined;

  @JsonProperty('fieldCurrentConsumption', 'Number')
  public fieldCurrentConsumption: number = undefined;

  @JsonProperty('fieldInternalCurrentConsumption', 'Number')
  public fieldInternalCurrentConsumption: number = undefined;

  @JsonProperty('measuredVoltage', 'Number')
  public measuredVoltage: number = undefined;

}
